/*
 * Copyright (c) 2026. Tooling Team
 */

import { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "@uhg-abyss/web/ui/Button";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import Layout from "../components/layout/Layout";
import { RouteDisplay } from "../components/navigation/RouteDisplay";
import { useGetLabDomainCodeEvidenceQuery } from "../app/services/labDomainApi";
import { PATHS } from "../utils/constants";
import { readLabMvdReturnState } from "../components/table/LabDomainCodeMapping/utils/navigationState";

const LAB_DOMAIN_EVIDENCE_ROW_ID = "__labDomainEvidenceRowId";

const formatColumnHeader = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.map(formatCellValue).join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const getColumnKeys = (rows) => [
  ...new Set(rows.flatMap((row) => Object.keys(row || {}))),
];

const getEvidenceRowId = (row, index) =>
  String(row?._id ?? row?.id ?? row?.evidenceId ?? index);

const getRowsWithInternalIds = (rows) =>
  rows.map((row, index) => ({
    ...row,
    [LAB_DOMAIN_EVIDENCE_ROW_ID]: getEvidenceRowId(row, index),
  }));

export function LabDomainCodeEvidence() {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const mapUuid = searchParams.get("mapUuid") || "";
  const localSystem = searchParams.get("localSystem") || "";
  const localCode = searchParams.get("localCode") || "";
  const hasMissingParams = !mapUuid || !localSystem || !localCode;

  const {
    data: codeEvidenceData = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetLabDomainCodeEvidenceQuery(
    { mapUuid, localSystem, localCode },
    { skip: hasMissingParams }
  );

  const rows = Array.isArray(codeEvidenceData) ? codeEvidenceData : [];
  const tableRows = useMemo(() => getRowsWithInternalIds(rows), [rows]);

  const columns = useMemo(() => {
    if (!rows.length) {
      return [];
    }

    return getColumnKeys(rows).map((key) => ({
      header: formatColumnHeader(key),
      accessorKey: key,
      cell: (cell) => formatCellValue(cell.getValue()),
    }));
  }, [rows]);

  const dataTableProps = useDataTable({
    initialData: tableRows,
    initialColumns: columns,
    rowIdKey: LAB_DOMAIN_EVIDENCE_ROW_ID,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
    },
  });

  useEffect(() => {
    if (typeof dataTableProps.setData === "function") {
      dataTableProps.setData(tableRows, true);
    }
  }, [tableRows, dataTableProps.setData]);

  useEffect(() => {
    if (typeof dataTableProps.setColumns === "function") {
      dataTableProps.setColumns(columns, true);
    }
  }, [columns, dataTableProps.setColumns]);

  const errorMessage =
    error?.data?.message ||
    error?.error ||
    "Unable to load Lab Domain code evidence.";

  const handleBackToMvd = () => {
    const labMvdReturnState =
      location.state?.labMvdReturnState || readLabMvdReturnState();

    if (labMvdReturnState) {
      history.push(PATHS.editMapMapValueDefinition.url, {
        labMvdReturnState,
      });
      return;
    }

    history.goBack();
  };

  return (
    <Layout>
      <RouteDisplay pageRouteDisplay={[PATHS.labDomainCodeEvidence]} />
      <div className="grid">
        <div className="row m-2 mb-4">
          <div className="col">
            <Button
              aria-label="Back to Map Value Definition"
              color="neutral"
              onClick={handleBackToMvd}
              variant="outline"
            >
              <IconSymbol icon="arrow_back" />
            </Button>
            <h3 className="mt-3">Lab Domain - Code Evidence</h3>
            <p>
              Map UUID: {mapUuid || "Missing"} | Local System:{" "}
              {localSystem || "Missing"} | Local Code: {localCode || "Missing"}
            </p>
          </div>
        </div>

        {hasMissingParams && (
          <div className="row m-2 mb-4">
            Missing required Lab Domain code evidence parameters.
          </div>
        )}

        {!hasMissingParams && (isLoading || isFetching) && !rows.length && (
          <div className="row m-2 mb-4">
            Loading Lab Domain code evidence data...
          </div>
        )}

        {!hasMissingParams && isError && (
          <div className="row m-2 mb-4">
            <p>{errorMessage}</p>
            <Button onClick={refetch}>Retry</Button>
          </div>
        )}

        {!hasMissingParams &&
          !isLoading &&
          !isFetching &&
          !isError &&
          rows.length === 0 && (
            <div className="row m-2 mb-4">
              No Lab Domain code evidence records were found.
            </div>
          )}

        {!hasMissingParams && !!rows.length && (
          <div className="row m-2 mb-4">
            <DataTable
              title="Lab Domain - Code Evidence"
              tableState={dataTableProps}
            >
              <DataTable.Table />
            </DataTable>
          </div>
        )}
      </div>
    </Layout>
  );
}
